/**
 * Session Manager
 * Handles session tracking and management across web and mobile platforms
 * Requirements: 7.4 - cross-platform session management
 */

import { getSupabaseClient, TABLES } from '@lumora/common';
import { Session } from '@supabase/supabase-js';

export interface SessionInfo {
  sessionId: string;
  userId: string;
  deviceId: string;
  platform: 'web' | 'mobile';
  userAgent: string;
  ipAddress?: string;
  lastActivity: string;
  expiresAt: string;
  isActive: boolean;
}

export class SessionManager {
  private static instance: SessionManager;
  private currentSession: Session | null = null;
  private sessionInfo: SessionInfo | null = null;
  private deviceId: string;

  private constructor() {
    this.deviceId = this.getOrCreateDeviceId();
    this.setupSessionListeners();
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Initialize session management
   * Requirements: 7.4 - cross-platform session management
   */
  async initialize(): Promise<void> {
    // We no longer set up listeners here to avoid race conditions with AuthProvider
    // AuthProvider will call onSessionStart/onSessionEnd
  }

  /**
   * Create a session record directly from a session object
   * This is useful when you already have a session and want to avoid calling getSession()
   */
  async createSession(session: Session): Promise<void> {
    await this.onSessionStart(session);
  }

  /**
   * Handle session start
   * Requirements: 7.4 - cross-platform session management
   */
  async onSessionStart(session: Session): Promise<void> {
    this.currentSession = session;

    // Get stable session ID from WebSessionManager
    const { webSessionManager } = await import('./auth/web-session');
    const webSession = webSessionManager.getCurrentSession();

    // Use webSession's sessionId if available, otherwise check if we already have
    // an active session in the database for this device/user combination
    let sessionId = webSession?.sessionId;

    if (!sessionId) {
      // Try to check if we already have an active session for this device and user
      // This query will only work if we have a valid token (which we should at this point)
      try {
        const supabase = getSupabaseClient();
        const { data: existingSessions, error } = await supabase
          .from(TABLES.USER_SESSIONS)
          .select('session_id')
          .eq('user_id', session.user.id)
          .eq('device_id', this.deviceId)
          .eq('is_active', true)
          .order('last_activity', { ascending: false })
          .limit(1);

        // If query succeeded and we found an existing session, reuse it
        if (!error && existingSessions && existingSessions.length > 0) {
          sessionId = existingSessions[0].session_id;
          console.log('♻️ Reusing existing session ID:', sessionId);
        } else {
          // No existing session found or query failed - create a stable ID
          // Use device ID + user ID for consistency across refreshes
          sessionId = `${this.deviceId}-${session.user.id}`.substring(0, 36);
          if (error) {
            console.warn(
              'Could not query existing sessions (token may be refreshing):',
              error.message
            );
          }
          console.log('🆕 Creating new session ID:', sessionId);
        }
      } catch (error) {
        // If anything fails (network, auth, etc), fall back to device-based ID
        console.error('Error checking for existing session:', error);
        sessionId = `${this.deviceId}-${session.user.id}`.substring(0, 36);
        console.log('⚠️ Using fallback session ID:', sessionId);
      }
    }

    // Final safety check - ensure sessionId is never undefined
    if (!sessionId) {
      sessionId = `${this.deviceId}-${session.user.id}`.substring(0, 36);
    }

    // Create session info
    this.sessionInfo = {
      sessionId: sessionId,
      userId: session.user.id,
      deviceId: this.deviceId,
      platform: 'web',
      userAgent: navigator.userAgent,
      lastActivity: new Date().toISOString(),
      expiresAt: new Date(session.expires_at! * 1000).toISOString(),
      isActive: true,
    };

    // Store session info in database
    await this.storeSessionInfo();

    // Start activity tracking and validity checking
    this.startActivityTracking();
    this.startValidityChecking();
  }

  /**
   * Handle session end and cleanup
   * Requirements: 7.4 - cross-platform session management
   */
  async onSessionEnd(): Promise<void> {
    if (this.sessionInfo) {
      // Mark session as inactive
      this.sessionInfo.isActive = false;
      await this.storeSessionInfo();
    }

    this.currentSession = null;
    this.sessionInfo = null;
    this.stopActivityTracking();
    this.stopValidityChecking();
  }

  /**
   * Clear the current session (public method for external use)
   */
  clearCurrentSession(): void {
    this.currentSession = null;
    this.sessionInfo = null;
    this.stopActivityTracking();
  }

  /**
   * Store session information in database (fire and forget - non-blocking)
   * Requirements: 7.4 - cross-platform session management
   * Note: DB session storage is best-effort and should not block auth flow
   */
  private async storeSessionInfo(): Promise<void> {
    if (!this.sessionInfo) return;

    // Don't try to update database if session is expired (will get 401)
    if (!this.currentSession) {
      return; // Silently skip - no session means nothing to store
    }

    // Check if session is expired - skip silently
    const now = Math.floor(Date.now() / 1000);
    if (
      this.currentSession.expires_at &&
      this.currentSession.expires_at < now
    ) {
      return; // Silently skip - expired session can't write to DB anyway
    }

    // Fire and forget - don't await, don't block
    this.storeSessionInfoAsync().catch(() => {
      // Silently ignore errors - DB session is supplementary, not critical
    });
  }

  /**
   * Actually store the session info (internal async helper)
   */
  private async storeSessionInfoAsync(): Promise<void> {
    if (!this.sessionInfo) return;

    try {
      const supabase = getSupabaseClient();

      const sessionRecord = {
        session_id: this.sessionInfo.sessionId,
        user_id: this.sessionInfo.userId,
        device_id: this.sessionInfo.deviceId,
        platform: this.sessionInfo.platform,
        user_agent: this.sessionInfo.userAgent,
        ip_address: this.sessionInfo.ipAddress,
        last_activity: this.sessionInfo.lastActivity,
        expires_at: this.sessionInfo.expiresAt,
        is_active: this.sessionInfo.isActive,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Upsert session record - ignore errors since this is best-effort
      await supabase.from(TABLES.USER_SESSIONS).upsert(sessionRecord, {
        onConflict: 'session_id',
      });
    } catch {
      // Silently ignore - DB session storage is supplementary
    }
  }

  /**
   * Update session activity timestamp
   * Requirements: 7.4 - cross-platform session management
   */
  private async updateSessionActivity(session: Session): Promise<void> {
    // Update current session with fresh token
    this.currentSession = session;

    if (this.sessionInfo) {
      this.sessionInfo.lastActivity = new Date().toISOString();
      this.sessionInfo.expiresAt = new Date(
        session.expires_at! * 1000
      ).toISOString();
      await this.storeSessionInfo();
    }
  }

  /**
   * Get active sessions for current user
   * Requirements: 7.4 - cross-platform session management
   */
  async getActiveSessions(): Promise<SessionInfo[]> {
    if (!this.currentSession) return [];

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase
        .from(TABLES.USER_SESSIONS)
        .select('*')
        .eq('user_id', this.currentSession.user.id)
        .eq('is_active', true)
        .order('last_activity', { ascending: false });

      if (error) {
        console.error('Failed to get active sessions:', error);
        return [];
      }

      return (data || []).map(record => ({
        sessionId: record.session_id,
        userId: record.user_id,
        deviceId: record.device_id,
        platform: record.platform,
        userAgent: record.user_agent,
        ipAddress: record.ip_address,
        lastActivity: record.last_activity,
        expiresAt: record.expires_at,
        isActive: record.is_active,
      }));
    } catch (error) {
      console.error('Error getting active sessions:', error);
      return [];
    }
  }

  /**
   * Terminate a specific session
   * Requirements: 7.4 - cross-platform session management
   */
  async terminateSession(sessionId: string): Promise<void> {
    try {
      const supabase = getSupabaseClient();

      const { error } = await supabase
        .from(TABLES.USER_SESSIONS)
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('session_id', sessionId);

      if (error) {
        console.error('Failed to terminate session:', error);
      }
    } catch (error) {
      console.error('Error terminating session:', error);
    }
  }

  /**
   * Terminate all other sessions except current
   * Requirements: 7.4 - cross-platform session management
   */
  async terminateOtherSessions(): Promise<void> {
    if (!this.sessionInfo) return;

    try {
      const supabase = getSupabaseClient();

      const { error } = await supabase
        .from(TABLES.USER_SESSIONS)
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', this.sessionInfo.userId)
        .neq('session_id', this.sessionInfo.sessionId);

      if (error) {
        console.error('Failed to terminate other sessions:', error);
      }
    } catch (error) {
      console.error('Error terminating other sessions:', error);
    }
  }

  /**
   * Get current session info
   */
  getCurrentSession(): SessionInfo | null {
    return this.sessionInfo;
  }

  /**
   * Get the underlying Supabase session
   */
  getSupabaseSession(): Session | null {
    return this.currentSession;
  }

  /**
   * Check if user has active sessions on other devices
   * Requirements: 7.4 - cross-platform session management
   */
  async hasOtherActiveSessions(): Promise<boolean> {
    const sessions = await this.getActiveSessions();
    return (
      sessions.length > 1 ||
      (sessions.length === 1 && sessions[0].deviceId !== this.deviceId)
    );
  }

  /**
   * Check if current session is still valid (not revoked)
   * Returns true if session is valid, false if revoked or not found
   * Requirements: 7.4 - cross-platform session management
   */
  async checkSessionValidity(): Promise<boolean> {
    if (!this.sessionInfo || !this.currentSession) {
      return false;
    }

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase
        .from(TABLES.USER_SESSIONS)
        .select('is_active')
        .eq('session_id', this.sessionInfo.sessionId)
        .single();

      if (error) {
        // If we can't check (e.g., network error), assume valid to avoid false logouts
        console.warn('Could not verify session validity:', error.message);
        return true;
      }

      if (!data || !data.is_active) {
        console.warn('🚫 Session has been revoked');
        // Emit session revoked event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('sessionRevoked'));
        }
        return false;
      }

      return true;
    } catch (error) {
      // On error, assume valid to avoid false logouts
      console.error('Error checking session validity:', error);
      return true;
    }
  }

  /**
   * Start periodic session validity checking
   * This ensures revoked sessions are enforced within a reasonable time
   */
  private validityCheckInterval: ReturnType<typeof setInterval> | null = null;

  private startValidityChecking(): void {
    // Check session validity every 30 seconds
    this.validityCheckInterval = setInterval(async () => {
      if (this.currentSession) {
        await this.checkSessionValidity();
      }
    }, 30 * 1000);
  }

  private stopValidityChecking(): void {
    if (this.validityCheckInterval) {
      clearInterval(this.validityCheckInterval);
      this.validityCheckInterval = null;
    }
  }

  // Private helper methods

  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('lumora_device_id');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('lumora_device_id', deviceId);
    }
    return deviceId;
  }

  private setupSessionListeners(): void {
    // Listen for visibility changes to update activity
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.currentSession) {
        this.updateSessionActivity(this.currentSession);
      }
    });
  }

  private activityInterval: ReturnType<typeof setInterval> | null = null;

  private startActivityTracking(): void {
    // Update activity every 5 minutes
    this.activityInterval = setInterval(
      () => {
        if (this.currentSession) {
          this.updateSessionActivity(this.currentSession);
        }
      },
      5 * 60 * 1000
    );
  }

  private stopActivityTracking(): void {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
      this.activityInterval = null;
    }
  }
}

export const sessionManager = SessionManager.getInstance();
